import { JsonType } from 'posthog-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { z } from 'zod';

import { Logger, useNamedLogger } from '@eluve/logger';
import {
  FeatureFlagDefinitions,
  FeatureFlagKeys,
  featureFlagsWithPayloads,
} from '@eluve/posthog';

import { usePostHog } from './usePostHog';

export type FeatureFlagContextType = {
  flagDefinitions: FeatureFlagDefinitions;
  getFeatureFlagData: (flagKey: FeatureFlagKeys) => JsonType;
  logger: Logger;
};

type FlagPayload<T extends FeatureFlagKeys> = z.infer<
  (typeof featureFlagsWithPayloads)[T]['schema']
>;

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const FeatureFlagProvider = FeatureFlagContext.Provider;

const useFeatureFlagContext = () => {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error(
      'useFeatureFlagContext must be used within a FeatureFlagProvider',
    );
  }

  return ctx;
};

export const useIsPostHogFeatureFlagEnabled = (flagKey: FeatureFlagKeys) => {
  const posthog = usePostHog();
  const read = useCallback(
    () => posthog.isFeatureEnabled(flagKey) ?? false,
    [posthog, flagKey],
  );
  const [enabled, setEnabled] = useState(read);

  useEffect(() => {
    // Re-read on mount: flags can settle between initial render and this effect, before onFeatureFlags subscribes.
    setEnabled(read());
    return posthog.onFeatureFlags(() => setEnabled(read()));
  }, [posthog, read]);

  return enabled;
};

export const POSTHOG_FLAGS_LOAD_TIMEOUT_MS = 3000;

// errorsLoading being a boolean distinguishes a real /flags response from a bootstrap-only callback.
// On remount, posthog fires onFeatureFlags synchronously from cache without errorsLoading, so we
// also seed initial state from posthog.featureFlags.hasLoadedFlags to avoid a 3s blank wait.
export const useArePostHogFlagsLoaded = (): boolean => {
  const posthog = usePostHog();
  const [loaded, setLoaded] = useState(
    () => posthog.featureFlags.hasLoadedFlags,
  );

  useEffect(() => {
    if (posthog.featureFlags.hasLoadedFlags) {
      setLoaded(true);
      return;
    }

    const unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
      if (typeof context?.errorsLoading !== 'boolean') return;
      setLoaded(true);
    });

    const timeout = setTimeout(() => {
      setLoaded(true);
    }, POSTHOG_FLAGS_LOAD_TIMEOUT_MS);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [posthog]);

  return loaded;
};

export const useIsV2AudioStackEnabled = () => {
  const logger = useNamedLogger('AudioStack');
  const flagEnabled = useIsPostHogFeatureFlagEnabled('AUDIO_STACK_V2');
  const forceBlobMode = useIsPostHogFeatureFlagEnabled(
    'AUDIO_STACK_V2_FORCE_BLOB',
  );
  const deprecateV1Audio = useIsPostHogFeatureFlagEnabled('DEPRECATE_V1_AUDIO');

  if (!flagEnabled) {
    // Rollout contract: DEPRECATE_V1_AUDIO is a mode of the V2 stack and has
    // no effect without AUDIO_STACK_V2. Diverged PostHog cohorts would
    // silently strand users on V1 — make that visible.
    if (deprecateV1Audio) {
      logger.warn(
        'DEPRECATE_V1_AUDIO is enabled but AUDIO_STACK_V2 is not — deprecation has no effect; user stays on V1.',
      );
    }
    return false;
  }

  if (forceBlobMode) return true;

  if (typeof AudioEncoder === 'undefined') {
    // V1-deprecation rollout: no-WebCodecs browsers (old Safari) run V2 in
    // blob mode (MediaRecorder capture + worker-side streaming remux)
    // instead of the legacy stack. Consumers derive `useBlobMode` from the
    // same AudioEncoder check, so returning true here selects blob.
    if (deprecateV1Audio) {
      logger.info(
        'AudioEncoder (WebCodecs) is not supported; using V2 blob mode (DEPRECATE_V1_AUDIO).',
      );
      return true;
    }
    logger.warn(
      'AUDIO_STACK_V2 flag is enabled but AudioEncoder (WebCodecs) is not supported. Falling back to V1.',
    );
    return false;
  }

  return true;
};

export const useFeatureFlagPayload = <T extends FeatureFlagKeys>(
  flagKey: T,
): FlagPayload<T> => {
  const { getFeatureFlagData, flagDefinitions } = useFeatureFlagContext();
  const posthog = usePostHog();

  const flagDefinition = flagDefinitions[flagKey];
  if (!flagDefinition) {
    throw new Error(`Missing flag definition for ${flagKey}`);
  }

  const { defaultValue, schema } = flagDefinition;

  const resolve = useCallback((): FlagPayload<T> => {
    const { success, data: parsed } = schema.safeParse(
      getFeatureFlagData(flagKey),
    );
    // TODO(jesse)[ELU-2301] - warn on schema mismatch once a PostHog proxy
    // stops adblockers from corrupting payloads.
    return (success ? parsed : defaultValue) as FlagPayload<T>;
  }, [getFeatureFlagData, flagKey, schema, defaultValue]);

  const [payload, setPayload] = useState<FlagPayload<T>>(resolve);

  useEffect(() => {
    // Re-read on mount to catch flags that settled before onFeatureFlags subscribed; for object payloads this costs one extra warm-load render (accepted trade-off).
    setPayload(resolve());
    return posthog.onFeatureFlags(() => setPayload(resolve()));
  }, [posthog, resolve]);

  return payload;
};
